import { useForm, Control, UseFormMethods, SubmitHandler } from 'react-hook-form';

import { useFlags } from 'launchdarkly-react-client-sdk';
import { useParams } from 'react-router-dom';
import {
  ProductCardButtonOption,
  ProductCardDefaultViewOption,
  ProductCardTextAlignmentOption,
  ProductCardPillDisplay,
} from 'types/graphql';

import useErnie from 'shared/hooks/use-ernie';
import { PreviewData } from '../components/preview';
import { useUpdateWebCustomizationSettingsV2, MenuCustomizationData } from '../data-access';
import { ProductCardsFormProps } from './product-card.types';

type HandlePublish = ReturnType<UseFormMethods<FormValues>['handleSubmit']>;

type FormValues = {
  defaultViewLarge: ProductCardDefaultViewOption;
  defaultViewSmall: ProductCardDefaultViewOption;
  fullWidthImages: boolean;
  disableProductCardImageCycling: boolean;
  mobileProductCardButton: ProductCardButtonOption;
  mobileShowCollection: boolean;
  mobileShowDiscountNames: boolean;
  mobileShowEffects: boolean;
  mobileShowPotencyDetails: boolean;
  mobilePotencyDisplay: ProductCardPillDisplay;
  mobileShowStaffPick: boolean;
  mobileShowStrainType: boolean;
  mobileStrainTypeDisplay: ProductCardPillDisplay;
  mobileBackgroundColor: string | null;
  mobileBrandColor: string | null;
  mobilePotencyColor: string | null;
  mobilePriceColor: string | null;
  mobileProductNameColor: string | null;
  mobileStrikethroughPriceColor: string | null;
  mobileWeightColor: string | null;
  showProductWeight: boolean;
  textAlignment: ProductCardTextAlignmentOption;
  // Only mounted when the ecomm.app.advanced-customization LaunchDarkly flag is on; optional
  // for the same reason other flag-gated color fields elsewhere in Customize are optional.
  strainTypeDefault?: string | null;
  strainTypeCbd?: string;
  strainTypeCbg?: string;
  strainTypeHighCBD?: string;
  strainTypeHybrid?: string;
  strainTypeIndica?: string;
  strainTypeIndicaHybrid?: string;
  strainTypeSativa?: string;
  strainTypeSativaHybrid?: string;
  strainTypeSeed?: string;
  strainTypeThc?: string;
};

type Input = {
  colors?: {
    strainTypes: {
      default: string | null;
      cbd: string | null;
      cbg: string | null;
      highCBD: string | null;
      hybrid: string | null;
      indica: string | null;
      indicaHybrid: string | null;
      sativa: string | null;
      sativaHybrid: string | null;
      seed: string | null;
      thc: string | null;
    };
  };
  productCards: {
    defaultViewLarge: ProductCardDefaultViewOption;
    defaultViewSmall: ProductCardDefaultViewOption;
    fullWidthImages: boolean;
    disableProductCardImageCycling: boolean;
    mobileAppDisplayOptions: {
      backgroundColor: string | null;
      brandColor: string | null;
      buttonDisplay: ProductCardButtonOption;
      potencyColor: string | null;
      potencyDisplay: ProductCardPillDisplay;
      priceColor: string | null;
      productNameColor: string | null;
      showCollection: boolean;
      showDiscountNames: boolean;
      showEffects: boolean;
      showPotencyDetails: boolean;
      showStaffPick: boolean;
      showStrainType: boolean;
      strainTypeDisplay: ProductCardPillDisplay;
      strikethroughPriceColor: string | null;
      weightColor: string | null;
    };
    showProductWeight: boolean;
    textAlignment: ProductCardTextAlignmentOption;
  };
};

type UseProductCardsFormReturn = {
  control: Control<FormValues>;
  handlePublish: HandlePublish;
  handleReset: (this: void) => void;
  isDirty: boolean;
  previewData: PreviewData;
  applyAllInput: Input;
  afterApplyAll: () => void;
};

export function useMergeDataWithFormValues(data: MenuCustomizationData, formValues: FormValues): PreviewData {
  const {
    defaultViewLarge,
    defaultViewSmall,
    fullWidthImages,
    disableProductCardImageCycling,
    mobileProductCardButton,
    mobileShowCollection,
    mobileShowDiscountNames,
    mobileShowEffects,
    mobileShowPotencyDetails,
    mobilePotencyDisplay,
    mobileShowStaffPick,
    mobileShowStrainType,
    mobileStrainTypeDisplay,
    mobileBackgroundColor,
    mobileBrandColor,
    mobilePotencyColor,
    mobilePriceColor,
    mobileProductNameColor,
    mobileStrikethroughPriceColor,
    mobileWeightColor,
    showProductWeight,
    textAlignment,
    strainTypeDefault,
    strainTypeCbd,
    strainTypeCbg,
    strainTypeHighCBD,
    strainTypeHybrid,
    strainTypeIndica,
    strainTypeIndicaHybrid,
    strainTypeSativa,
    strainTypeSativaHybrid,
    strainTypeSeed,
    strainTypeThc,
  } = formValues;

  return {
    ...data,
    colors: {
      ...data.colors,
      strainTypes: {
        default: strainTypeDefault ?? null,
        cbd: strainTypeCbd ?? null,
        cbg: strainTypeCbg ?? null,
        highCBD: strainTypeHighCBD ?? null,
        hybrid: strainTypeHybrid ?? null,
        indica: strainTypeIndica ?? null,
        indicaHybrid: strainTypeIndicaHybrid ?? null,
        sativa: strainTypeSativa ?? null,
        sativaHybrid: strainTypeSativaHybrid ?? null,
        seed: strainTypeSeed ?? null,
        thc: strainTypeThc ?? null,
      },
    },
    productCards: {
      defaultViewLarge,
      defaultViewSmall,
      fullWidthImages,
      disableProductCardImageCycling,
      mobileAppDisplayOptions: {
        backgroundColor: mobileBackgroundColor,
        brandColor: mobileBrandColor,
        buttonDisplay: mobileProductCardButton,
        potencyColor: mobilePotencyColor,
        potencyDisplay: mobilePotencyDisplay,
        priceColor: mobilePriceColor,
        productNameColor: mobileProductNameColor,
        showCollection: mobileShowCollection,
        showDiscountNames: mobileShowDiscountNames,
        showEffects: mobileShowEffects,
        showPotencyDetails: mobileShowPotencyDetails,
        showProductWeight,
        showStaffPick: mobileShowStaffPick,
        showStrainType: mobileShowStrainType,
        strainTypeDisplay: mobileStrainTypeDisplay,
        strikethroughPriceColor: mobileStrikethroughPriceColor,
        weightColor: mobileWeightColor,
      },
      showProductWeight,
      textAlignment,
    },
  };
}

export function formatInput(values: FormValues, includeStrainType: boolean): Input {
  const input: Input = {
    productCards: {
      defaultViewLarge: values.defaultViewLarge,
      defaultViewSmall: values.defaultViewSmall,
      fullWidthImages: values.fullWidthImages,
      disableProductCardImageCycling: values.disableProductCardImageCycling,
      mobileAppDisplayOptions: {
        backgroundColor: values.mobileBackgroundColor,
        brandColor: values.mobileBrandColor,
        buttonDisplay: values.mobileProductCardButton,
        potencyColor: values.mobilePotencyColor,
        potencyDisplay: values.mobilePotencyDisplay,
        priceColor: values.mobilePriceColor,
        productNameColor: values.mobileProductNameColor,
        showCollection: values.mobileShowCollection,
        showDiscountNames: values.mobileShowDiscountNames,
        showEffects: values.mobileShowEffects,
        showPotencyDetails: values.mobileShowPotencyDetails,
        showStaffPick: values.mobileShowStaffPick,
        showStrainType: values.mobileShowStrainType,
        strainTypeDisplay: values.mobileStrainTypeDisplay,
        strikethroughPriceColor: values.mobileStrikethroughPriceColor,
        weightColor: values.mobileWeightColor,
      },
      showProductWeight: values.showProductWeight,
      textAlignment: values.textAlignment,
    },
  };

  if (includeStrainType) {
    input.colors = {
      strainTypes: {
        default: values.strainTypeDefault ?? null,
        cbd: values.strainTypeCbd ?? null,
        cbg: values.strainTypeCbg ?? null,
        highCBD: values.strainTypeHighCBD ?? null,
        hybrid: values.strainTypeHybrid ?? null,
        indica: values.strainTypeIndica ?? null,
        indicaHybrid: values.strainTypeIndicaHybrid ?? null,
        sativa: values.strainTypeSativa ?? null,
        sativaHybrid: values.strainTypeSativaHybrid ?? null,
        seed: values.strainTypeSeed ?? null,
        thc: values.strainTypeThc ?? null,
      },
    };
  }

  return input;
}

export function useProductCardsForm({ data }: ProductCardsFormProps): UseProductCardsFormReturn {
  const {
    colors: { strainTypes },
    productCards: {
      defaultViewLarge,
      defaultViewSmall,
      fullWidthImages,
      disableProductCardImageCycling,
      mobileAppDisplayOptions,
      showProductWeight,
      textAlignment,
    },
  } = data;

  const defaultValues = {
    defaultViewLarge,
    defaultViewSmall,
    fullWidthImages,
    disableProductCardImageCycling,
    mobileProductCardButton: mobileAppDisplayOptions.buttonDisplay ?? ProductCardButtonOption.icon,
    mobileShowCollection: mobileAppDisplayOptions.showCollection ?? false,
    mobileShowDiscountNames: mobileAppDisplayOptions.showDiscountNames ?? false,
    mobileShowEffects: mobileAppDisplayOptions.showEffects ?? false,
    mobileShowPotencyDetails: mobileAppDisplayOptions.showPotencyDetails ?? false,
    mobilePotencyDisplay: mobileAppDisplayOptions.potencyDisplay ?? ProductCardPillDisplay.text,
    mobileShowStaffPick: mobileAppDisplayOptions.showStaffPick ?? false,
    mobileShowStrainType: mobileAppDisplayOptions.showStrainType ?? false,
    mobileStrainTypeDisplay: mobileAppDisplayOptions.strainTypeDisplay ?? ProductCardPillDisplay.filled,
    mobileBackgroundColor: mobileAppDisplayOptions.backgroundColor ?? null,
    mobileBrandColor: mobileAppDisplayOptions.brandColor ?? null,
    mobilePotencyColor: mobileAppDisplayOptions.potencyColor ?? null,
    mobilePriceColor: mobileAppDisplayOptions.priceColor ?? null,
    mobileProductNameColor: mobileAppDisplayOptions.productNameColor ?? null,
    mobileStrikethroughPriceColor: mobileAppDisplayOptions.strikethroughPriceColor ?? null,
    mobileWeightColor: mobileAppDisplayOptions.weightColor ?? null,
    showProductWeight,
    textAlignment,
    strainTypeDefault: strainTypes?.default ?? null,
    strainTypeCbd: strainTypes?.cbd ?? '',
    strainTypeCbg: strainTypes?.cbg ?? '',
    strainTypeHighCBD: strainTypes?.highCBD ?? '',
    strainTypeHybrid: strainTypes?.hybrid ?? '',
    strainTypeIndica: strainTypes?.indica ?? '',
    strainTypeIndicaHybrid: strainTypes?.indicaHybrid ?? '',
    strainTypeSativa: strainTypes?.sativa ?? '',
    strainTypeSativaHybrid: strainTypes?.sativaHybrid ?? '',
    strainTypeSeed: strainTypes?.seed ?? '',
    strainTypeThc: strainTypes?.thc ?? '',
  };

  const {
    control,
    formState: { isDirty },
    handleSubmit,
    reset,
    // eslint-disable-next-line @typescript-eslint/unbound-method
    watch,
  } = useForm<FormValues>({
    // Same reason as use-theme-form.ts: v6 measures `isDirty` against the full `defaultValues`
    // using only the currently registered fields, and mobileOnly never registers the desktop/web
    // block — so unregistering a conditional field (Strain Type Display, Potency Display) reports
    // an unedited form as dirty. Keeping unmounted fields registered keeps the comparison honest.
    shouldUnregister: false,
    defaultValues,
  });

  const { id: dispensaryId } = useParams<{ id: string }>();

  const showErnie = useErnie();

  const updateData = useUpdateWebCustomizationSettingsV2();

  const flags = useFlags();
  const includeStrainType = flags['ecomm.app.advanced-customization'] ?? false;

  const onSubmit: SubmitHandler<FormValues> = async (submittedValues) => {
    // react-hook-form's handleSubmit callback only includes fields that are (or were) actually
    // registered by a mounted <Controller> — in mobileOnly mode (see
    // ProductCardsFormProps.mobileOnly), an entire block of fields (Default View, Display
    // Product Weight, Full Width Images, Image Cycling, Text Alignment, Strain Type Colors)
    // never mounts a Controller at all, so `submittedValues` is *missing those keys entirely*,
    // not just `undefined`-valued. Merging over `watch()` (which always falls back to the form's
    // defaultValues for fields with no registered input) backfills them with their
    // current/loaded value instead of silently dropping them from the save payload and, after
    // the reset() below, from the live preview.
    const completeValues = { ...watch(), ...submittedValues };
    const input = formatInput(completeValues, includeStrainType);

    try {
      await updateData({
        variables: {
          dispensaryId,
          input,
        },
      });

      showErnie('Your product card settings have been updated', 'success');
      reset({ ...completeValues });
    } catch (err) {
      showErnie('Something went wrong, please try again.', 'danger');
      console.error(err);
    }
  };

  const handlePublish = handleSubmit(onSubmit);

  const handleReset = (): void => reset();

  const formValues = watch();

  const previewData = useMergeDataWithFormValues(data, formValues);

  const applyAllInput = formatInput(formValues, includeStrainType);

  const afterApplyAll = (): void => reset({ ...formValues });

  return {
    control,
    handlePublish,
    handleReset,
    isDirty,
    previewData,
    applyAllInput,
    afterApplyAll,
  };
}
